/* eslint-disable indent */
/* eslint-disable no-nested-ternary */
import React, { useState, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import { checkErrorResponse, Logger, LoggerTypes } from '../../../../../utils'
import { DialogFormEdit } from '../../../../dialogs'
import { AttributeWeatherForm } from '../../AttributeWeatherForm'

export const AttributeWeatherDialogUpdate = forwardRef(
  ({ currentProject, currentScenario, openDialog, setOpenDialog, weather, actionUpdateDispatch }, ref) => {
    const [errors, setErrors] = useState(null)
    const [progress, setProgress] = useState(null)
    const [isSuccess, setIsSuccess] = useState(false)

    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { t } = useTranslation()

    const title = currentScenario
      ? t('attributes.weather_data.update.scenario')
      : currentProject
      ? t('attributes.weather_data.update.project')
      : t('attributes.weather_data.update.global')

    const onUploadProgress = (_progress) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeWeatherDialogCreate.onUploadProgress()', { _progress })

      setProgress(_progress)
    }

    /**
     * Action received from the Weather Form.
     * @see AttributeWeatherForm
     *
     * @param {object} newWeather Weather attribute object to update.
     * @param {string} newWeatherId Weather attribute Id to update.
     * @param {func} onSuccess Function to execute on success.
     */
    const onDispatchAction = (newWeather, newWeatherId, onSuccess) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeWeatherDialogUpdate.onDispatchAction()', {
        newWeather,
        newWeatherId,
        actionUpdateDispatch
      })

      setErrors(null)

      dispatch(
        actionUpdateDispatch(newWeather, newWeatherId, onUploadProgress, currentProject?.id, currentScenario?.id)
      )
        .then((res) => {
          Logger.trace(
            LoggerTypes.CONTROLLER,
            'AttributeWeatherDialogUpdate.onDispatchAction().dispatch(actionUpdateDispatch(...)).then()',
            {
              res
            }
          )

          setIsSuccess(true)

          if (res?.data?.isAxiosError) {
            enqueueSnackbar(t('common.api_failure'), { variant: 'error' })
          } else if (onSuccess && typeof onSuccess === 'function') {
            onSuccess(res)
          }
        })
        .catch((res) => {
          Logger.trace(
            LoggerTypes.WARN,
            'AttributeWeatherDialogUpdate.onDispatchAction().dispatch(actionUpdateDispatch(...)).catch()',
            { res }
          )

          checkErrorResponse(res, enqueueSnackbar, t)

          if (res?.response?.data?.fields) {
            setErrors(res.response.data.fields)
          }

          setIsSuccess(false)
        })
    }

    Logger.trace(LoggerTypes.CONTAINER, 'AttributeWeatherDialogUpdate.render()', { weather })

    return (
      <DialogFormEdit className="attribute-form" openDialog={openDialog} setOpenDialog={setOpenDialog}>
        <AttributeWeatherForm
          ref={ref}
          title={title}
          setOpenDialogWeather={setOpenDialog}
          weather={weather}
          dispatchAction={onDispatchAction}
          errors={errors}
          progress={progress}
          isSuccess={isSuccess}
        />
      </DialogFormEdit>
    )
  }
)

AttributeWeatherDialogUpdate.displayName = 'AttributeWeatherDialogUpdate'

AttributeWeatherDialogUpdate.propTypes = {
  currentProject: PropTypes.object,
  currentScenario: PropTypes.object,
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired,
  actionUpdateDispatch: PropTypes.func,
  weather: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    initial_value: PropTypes.number,
    units: PropTypes.arrayOf(PropTypes.string)
  }).isRequired
}

AttributeWeatherDialogUpdate.defaultProps = {
  currentProject: null,
  currentScenario: null,
  openDialog: false,
  setOpenDialog: () => {},
  actionUpdateDispatch: null,
  weather: {
    name: '',
    description: '',
    initial_value: '',
    type: null,
    unit: ''
  }
}
